import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import DTSearchView from '../../Common/DTSearchView/DTSearchView';
import {
  chooseDefaultSearchValue,
  getReturnSearchQuery,
  getReturnsSearchProperties,
  getSelectedReturn,
  getSelectedReturnsSearchAction,
} from '../../../../selectors/tools';
import { trackReturnSearchQuerySubmitted } from '../../../../analytics/searchEventAnalytics';
import * as returnsSearchOptions from '../../../../constants/returnSearchOptions';
import { displayPopupMessageInformation } from '../../../../components/tools/SearchPage/searchUtils';
import { setSearchReturnsQuery } from '../../../../actions/tools/CommonInterface/Returns/ReturnActions';
import { searchReturns } from '../../../../actions/tools/CommonInterface/Returns/AsyncReturnActions';
import { addEdgeToDecisionTreePath } from '../../../../actions/searchActions';
import DTNoResults from '../../Common/DTNoResults/DTNoResults';
import { noResultConfig } from '../../DTInfoWidget/widgetConfig';
import SearchOptions from './SearchOptions/SearchOptions';
import SearchResults from './SearchResults/SearchResults';
import { NoResultsWrapper, Title } from './DTSearchReturnsNode.styles';
import { handleError } from 'services/ErrorService/ErrorService';

const DTSearchReturnsNode = () => {
  const dispatch = useDispatch();

  const selectedReturn = useSelector(getSelectedReturn);
  const searchValue = useSelector(getReturnSearchQuery);
  const defaultSearchValue = useSelector(chooseDefaultSearchValue);
  const selectedSearchAction = useSelector(getSelectedReturnsSearchAction);
  const returnsSearch = useSelector(getReturnsSearchProperties);

  const handleSearchReturns = useCallback(() => {
    const searchStr = (searchValue && searchValue.trim()) || '';

    trackReturnSearchQuerySubmitted({ action: selectedSearchAction, searchQuery: searchStr });

    switch (selectedSearchAction) {
      case returnsSearchOptions.SEARCH_RETURNS_BY_ORDER_NUMBER:
        if (!searchStr) {
          displayPopupMessageInformation('Please enter a valid order number');
          break;
        }

        return dispatch(searchReturns({ orderNumber: searchStr }));
      default:
        handleError({ customMessage: `Unknown action: ${selectedSearchAction}` });
    }
  }, [dispatch, searchValue, selectedSearchAction]);

  useEffect(() => {
    if (defaultSearchValue && !searchValue) {
      dispatch(setSearchReturnsQuery(defaultSearchValue));
    }
  }, [dispatch, defaultSearchValue, searchValue]);

  useEffect(() => {
    if (searchValue) {
      handleSearchReturns();
    }
  }, [searchValue, handleSearchReturns]);

  const handleSearchSubmit = (value) => {
    dispatch(setSearchReturnsQuery(value));
  };

  const handleNextButtonClick = () => dispatch(addEdgeToDecisionTreePath('items_selected'));

  return (
    <DTSearchView
      isNextButtonActive={!isEmpty(selectedReturn)}
      onNextButtonClick={handleNextButtonClick}
      handleSearchSubmit={handleSearchSubmit}
      searchType={selectedSearchAction}
      searchValue={searchValue}
      defaultValue={defaultSearchValue}
      dataToDisplay={returnsSearch?.returns}
      searchPageSelectComponent={<SearchOptions />}
      searchResultsComponent={<SearchResults />}
      noDataMessageComponent={
        <NoResultsWrapper key={'noReturnsFound'}>
          <Title>No returns found</Title>
          <DTNoResults {...noResultConfig.returnsCommon} />
        </NoResultsWrapper>
      }
    />
  );
};

export default DTSearchReturnsNode;
